import { useEffect } from "react";
import { Provider } from "react-redux";
import Head from "next/head";
import { useRouter } from "next/router";
import { Poppins } from "next/font/google";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-phone-number-input/style.css";
import "@/styles/globals.css";
import "@/styles/no-js.css";
import { store } from "@/store";
import { AlertProvider } from "@/Common/CustomAlert/AlertContext";
import { AuthProvider } from "@/Common/Auth/AuthContext";
import RegionRedirect from "@/Component/Common/RegionRedirect";
import SiteOverlays from "@/Component/Common/SiteOverlays";
import FirebaseMessaging from "@/Component/Common/FirebaseMessaging";
import UserPageTracking from "@/Component/Common/UserPageTracking";
import { installReloadGuard } from "@/utils/disableAutomaticReloads";
import { VIEWPORT_CONTENT, enforceViewport } from "@/utils/viewport";

if (typeof window !== "undefined") {
  installReloadGuard();
}

const poppins = Poppins({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
  variable: "--font-poppins",
  display: "swap",
});

export default function App({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    enforceViewport();
    router.events.on("routeChangeComplete", enforceViewport);
    return () => router.events.off("routeChangeComplete", enforceViewport);
  }, [router.events]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    installReloadGuard();

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <Provider store={store}>
      <AlertProvider>
        <AuthProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <>
              <Head>
                <meta name="viewport" content={VIEWPORT_CONTENT} key="viewport" />
              </Head>
              <style jsx global>{`
                html,
                body {
                  font-family: ${poppins.style.fontFamily}, "Poppins", sans-serif;
                }
              `}</style>
              <div className={`${poppins.variable} ${poppins.className}`}>
                <RegionRedirect />
                <FirebaseMessaging />
                <UserPageTracking />
                <Component {...pageProps} />
                <SiteOverlays />
              </div>
            </>
          </LocalizationProvider>
        </AuthProvider>
      </AlertProvider>
    </Provider>
  );
}
